import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { addTickerToWatchlist } from '../watchlist';
import { showToast } from '../toast';

interface Buyer { owner: string; role: string; value: number; }
interface Cluster { ticker: string; company: string; nBuyers: number; total: number; lastDate: string; buyers: Buyer[]; }
interface SmartMoneyResp { clusters: Cluster[]; scannedAt?: string; windowDays?: number; note?: string; }

function fmt$(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}
function fmtDate(ymd: string): string {
  return ymd && ymd.length === 8 ? `${ymd.slice(4, 6)}/${ymd.slice(6, 8)}` : ymd;
}

// Smart Money — market-wide insider Form-4 cluster buys (open-market, code P) from the host scanner.
export class SmartMoneyTab implements Tab {
  readonly id = 'smartmoney';
  private loaded = false;
  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    document.getElementById('sm-refresh')?.addEventListener('click', () => { this.loaded = false; this.load(); });
  }
  render(): void { if (!this.loaded) this.load(); }

  private async load(): Promise<void> {
    const body = this.el('sm-body');
    body.innerHTML = `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Loading market-wide insider cluster buys…</div>`;
    try {
      const d = await workerFetch<SmartMoneyResp>('/api/smart-money');
      const clusters = d.clusters ?? [];
      const upd = document.getElementById('sm-updated');
      if (upd) upd.textContent = d.scannedAt
        ? `Scanned ${timeAgo(d.scannedAt)} · ${clusters.length} clusters · ${d.windowDays ?? '?'}d window`
        : (d.note ?? '');
      if (!clusters.length) {
        body.innerHTML = `<div style="padding:24px;color:var(--text3);font-size:12px;">No insider cluster buys in the window yet — the scanner pushes daily after EDGAR filings post.</div>`;
        this.loaded = true; return;
      }
      body.innerHTML = this.table(clusters);
      body.querySelectorAll<HTMLButtonElement>('.sm-add').forEach(btn =>
        btn.addEventListener('click', () => {
          const tk = btn.dataset.tk!;
          addTickerToWatchlist(tk);
          btn.textContent = '✓ added'; btn.disabled = true; btn.style.opacity = '0.5';
          showToast(`${tk} added to watchlist`, 'success');
        }));
      this.loaded = true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      body.innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private table(cs: Cluster[]): string {
    const th = (t: string) => `<th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">${t}</th>`;
    const rows = cs.map(c => {
      const buyers = c.buyers.slice(0, 4).map(b =>
        `<div style="font-size:11px;line-height:1.5;"><span style="color:var(--text);">${esc(b.owner)}</span> <span style="color:var(--text3);">${esc(b.role)}</span> · <span style="color:var(--green);">${fmt$(b.value)}</span></div>`).join('');
      return `<tr style="border-bottom:1px solid var(--border);vertical-align:top;">
        <td style="padding:10px 14px;font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(c.ticker)}</td>
        <td style="padding:10px 14px;font-size:12px;color:var(--text);">${esc(c.company)}</td>
        <td style="padding:10px 14px;font-family:var(--mono);font-weight:700;color:var(--green);">${c.nBuyers} 🟢</td>
        <td style="padding:10px 14px;font-family:var(--mono);font-weight:700;">${fmt$(c.total)}</td>
        <td style="padding:10px 14px;font-family:var(--mono);font-size:11px;color:var(--text3);">${fmtDate(c.lastDate)}</td>
        <td style="padding:10px 14px;">${buyers}</td>
        <td style="padding:10px 14px;"><button class="sm-add" data-tk="${esc(c.ticker)}" style="cursor:pointer;font-family:var(--mono);font-size:11px;padding:4px 10px;border-radius:4px;border:1px solid var(--accent);background:transparent;color:var(--accent);white-space:nowrap;">+ Watchlist</button></td>
      </tr>`;
    }).join('');
    return `<table style="width:100%;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border);">${th('Ticker')}${th('Company')}${th('Buyers')}${th('Total $')}${th('Last')}${th('Insiders')}${th('')}</tr></thead>
      <tbody>${rows}</tbody></table>
      <div style="font-size:10px;color:var(--text3);margin-top:12px;font-style:italic;">
        Open-market insider BUYS (SEC Form-4 code P), clustered by issuer (≥2 distinct insiders) over the scan window — free EDGAR, market-wide.
        Discovery / context, not a validated signal (the cluster-buy edge gauntlet'd NO-GO). Click <strong>+ Watchlist</strong> to track a name.
      </div>`;
  }
}
